import {
  Grid,
  FormField,
  TextInput,
  Flexbox,
  SingleDatePicker,
  type FormLabelIconPosition,
} from '@sede-enterprise/shell-ds-react-framework';
import filterOptions from '../../../constants/filterOptions';
import { useContext, useState } from 'react';
import { AppContext } from '../../../context/calculatorFormContext';
import {
  onOpportunityChange,
  handleEvpChange,
  handleBusinessChange,
  handleCountyChange,
  handleCommodityChange,
  handleTechSourceChange,
  handleGridLocationChange,
  handleAssetTypeChange,
  handleFuelTypeChange,
  handleOperatingModelChange,
  onEquityPercentageChange,
  maxLengthCheck,
  onEfficiencyPercentageChange,
  onGrossMarginChange,
  handleStartDateChange,
  handleEndDateChange,
  handleRegionChange,
  handleTypeOfSupplyChange,
} from './KpiFormFunctions';
import { ReusableSelect } from './ReusableSelet';
import {
  type IDispatch,
  TChangeEvent,
  StringDispatch,
  IManualFormData,
  eventType,
  DefaultOptionType,
} from '../../../types/types';
import dayjs from 'dayjs';
import { one } from '../../../constants';
import {
  DR_POWER_OTHERS,
  TYPE_OF_SUPPLY_OPTIONS,
  SLMT_COMMODITY_OPTIONS,
  COUNTRY_ASSET_OF_SUPPLY,
  COUNTRY,
  TYPE_OF_SUPPLY,
} from '../../../api/constants';
import { capitalizeFirstLetter } from '../../AllDeals/DealDetails/DealDetailFunctions';

// Helper function to normalize EVP name for comparison
const normalizeEvpName = (evpName: string): string => {
  return evpName.toLowerCase().replace(/[^a-z0-9]/g, '');
};

export const KpiForm = (props: {
  setCountry: StringDispatch;
  myState: IManualFormData;
  myDispatch: IDispatch;
  country: string;
  isAccordionOpen: boolean;
}) => {
  const { state, dispatch } = useContext(AppContext);
  const { setCountry, myState, myDispatch, country } = props;
  const [isFetchingGridLocation, setIsFetchingGridLocation] = useState(false);
  const [isFetchingCountry, setIsFetchingCountry] = useState(false);
  const [gridLocationOptions, setGridLocationOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const { countryOptions, regionOptions } = state;
  const { operatingModelOptions } = filterOptions;
  const { unitofmeasuresOptns } = filterOptions;
  const isTSProducts = normalizeEvpName(state.evp) === 'tsproducts';

  const selectConfig = {
    evp: {
      id: 'evp',
      label: 'EVP',
      mandatory: true,
      value: state.evp,
      onChange: (_e: eventType, option: DefaultOptionType) => {
        void handleEvpChange(_e, { option, myState }, dispatch, myDispatch, {
          setGridLocationOptions,
          setCountry,
        });
      },
      optionLabelProp: 'label',
      invalid: !myState.isEvpValid,
      ariaLabel: 'Select',
      allowClear: false,
      options: myState.evpOptions,
    },
    business: {
      id: 'business',
      label: 'Business',
      mandatory: true,
      value: state.business,
      onChange: (_e: eventType, option: DefaultOptionType) => {
        void handleBusinessChange(_e, { myState, option, state, country }, dispatch, myDispatch, {
          setGridLocationOptions,
          setIsFetchingCountry,
          setCountry,
          setIsFetchingGridLocation,
        });
      },
      optionLabelProp: 'label',
      invalid: !myState.isBusinessValid,
      ariaLabel: 'Select',
      mode: normalizeEvpName(state.evp) === 'tsproducts' ? 'multiple' : 'single',
      allowClear: false,
      options: myState.businessunitsOptns,
    },
    region: {
      id: 'region',
      label: 'Region',
      mandatory: true,
      value: state.region,
      onChange: (_e: eventType, option: DefaultOptionType) => {
        void handleRegionChange(_e, { myState, option, state, country }, dispatch, myDispatch, {
          setCountry,
          setIsFetchingCountry,
          setIsFetchingGridLocation,
          setGridLocationOptions,
        });
      },
      invalid: !myState.isRegionValid,
      disabled: regionOptions.length <= one,
      options: regionOptions,
    },
    country: {
      id: 'country',
      label: normalizeEvpName(state.evp) === 'slmt' ? COUNTRY_ASSET_OF_SUPPLY : COUNTRY,
      mandatory: true,
      size: 'medium',
      allowClear: false,
      value: country,
      loading: isFetchingCountry,
      onChange: (_e: eventType, option: DefaultOptionType) => {
        handleCountyChange(_e, option, country, {
          dispatch,
          myDispatch,
          setCountry,
          myState,
          setIsFetchingGridLocation,
          state,
          setGridLocationOptions,
        });
      },
      invalid: !myState.isCountryValid,
      disabled: countryOptions.length <= one,
      options: countryOptions,
    },
    typeOfSupply: {
      id: 'typeOfSupply',
      label: TYPE_OF_SUPPLY,
      mandatory: true,
      value: (() => {
        if (normalizeEvpName(state.evp) === 'slmt') {
          const selected = myState.typeOfSupplyOptions.find(opt => String(opt.label) === String(state.typeOfSupply));
          return selected ? String(selected.label) : '';
        }
        return String(state.typeOfSupply);
      })(),
      onChange: (_e: eventType, option: DefaultOptionType) => {
        void handleTypeOfSupplyChange(_e, option, dispatch, myDispatch, state, {
          setCountry,
          setIsFetchingCountry,
          setGridLocationOptions,
          setIsFetchingGridLocation,
        });
      },
      optionLabelProp: 'label',
      invalid: !myState.isTypeOfSupplyValid,
      allowClear: false,
      options: normalizeEvpName(state.evp) === 'slmt' ? (myState.typeOfSupplyOptions || []) : TYPE_OF_SUPPLY_OPTIONS,
    },
    commodity: {
      id: 'commodity',
      label: normalizeEvpName(state.evp) === 'tsproducts' ? 'Product' : 'Commodity',
      mandatory: true,
      value: state.commodity,
      onChange: (_e: eventType, option: DefaultOptionType) => {
        handleCommodityChange(_e, option, dispatch, myDispatch, myState);
      },
      disabled: state.business === DR_POWER_OTHERS,
      optionLabelProp: 'label',
      invalid: !myState.isCommodityValid,
      mode: normalizeEvpName(state.evp) === 'tsproducts' ? 'multiple' : 'single',
      allowClear: false,
      options:
        normalizeEvpName(state.evp) === 'slmt' ? SLMT_COMMODITY_OPTIONS : myState.commodityOptions,
    },
    techSource: {
      id: 'techSource',
      label: 'Technology Source',
      mandatory: true,
      value: myState.techSource,
      onChange: (e: eventType, _option: DefaultOptionType) => {
        handleTechSourceChange(e, _option, gridLocationOptions, {
          dispatch,
          myDispatch,
          state,
          country,
          myState,
          setIsFetchingGridLocation,
          setGridLocationOptions,
        });
      },
      optionLabelProp: 'label',
      disabled: !myState.enableTechSourceField,
      invalid: !myState.isTechSourceValid,
      allowClear: false,
      options: myState.techSourceOptions,
      mode: 'multiple',
    },
    gridLocation: {
      id: 'gridLocation',
      label: 'Grid Location',
      mandatory: true,
      value: capitalizeFirstLetter(state.gridLocation),
      onChange: (e: eventType, option: DefaultOptionType) => {
        handleGridLocationChange(e, option, dispatch, myDispatch);
      },
      loading: isFetchingGridLocation,
      disabled: myState.disableGrid,
      optionLabelProp: 'label',
      invalid: !myState.isGridLocationValid,
      allowClear: false,
      options: gridLocationOptions,
    },
    assetType: {
      id: 'assetType',
      label: 'Asset Type',
      mandatory: true,
      value: state.assetType,
      onChange: (e: eventType) => {
        handleAssetTypeChange(e, dispatch, myDispatch);
      },
      optionLabelProp: 'label',
      invalid: !myState.isAssetTypeValid,
      allowClear: false,
      options: normalizeEvpName(state.evp) === 'slmt' 
        ? (myState.countryAssetOfSupplyOptions || [])
        : (myState.assetTypeOptions || []),  // ✅ Shows Brownfield, Greenfield for non-SLMT EVPs and specific asset types for SLMT based on country
    },
    fuelType: {
      id: 'fuelType',
      label: 'Fuel Type',
      mandatory: true,
      value: state.fuelType,
      onChange: (e: eventType) => {
        handleFuelTypeChange(e, dispatch, myDispatch, state, myState);
      },
      invalid: !myState.isFuelTypeValid,
      allowClear: false,
      options: myState.fuelTypeOptions,
    },
    operatingModel: {
      id: 'operatingModel',
      label: 'Operating Model',
      mandatory: true,
      onChange: (_e: eventType, options: DefaultOptionType) => {
        handleOperatingModelChange(_e, options, dispatch, myDispatch);
      },
      optionLabelProp: 'label',
      invalid: !myState.isOperatingModelValid,
      allowClear: false,
      options: operatingModelOptions,
    },
    unitOfMeasure: {
      id: 'unitOfMeasure',
      label: 'Unit of Measure',
      mandatory: true,
      value: myState.uomVal,
      optionLabelProp: 'label',
      allowClear: false,
      options: unitofmeasuresOptns,
      iconPosition: 'right' as FormLabelIconPosition,
      disabled: true,
    },
    carbonCredit: {
      id: 'carbonCredit',
      label: 'Carbon Credit',
      mandatory: true,
      value: state.carbonCredit === 1 ? 'Yes' : state.carbonCredit === 0 ? 'No' : '',
      onChange: (_e: eventType, option: DefaultOptionType) => {
        const value = option.value === 'Yes' ? 1 : 0;
        dispatch({
          type: 'setCarbonCredit',
          payload: value,
        });
      },
      optionLabelProp: 'label',
      invalid: !myState.isCarbonCreditValid,
      allowClear: false,
      options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
      ],
    },
  };
  const getDayjsFormat = (selectedDate: string) => {
    return selectedDate ? dayjs(selectedDate) : null;
  };
  return (
    <>
      <Grid columns={1} style={{ marginTop: '10px' }}>
        <FormField id='opportunityName' label='Opportunity Name' mandatory={true}>
          <TextInput
            aria-label='Opportunity Name'
            onChange={(e: TChangeEvent) => {
              onOpportunityChange(e, dispatch, myDispatch);
            }}
            value={state.opportunity}
            maxLength={100}
            invalid={!myState.isOpportunityValid}
          />
        </FormField>
      </Grid>
      {normalizeEvpName(state.evp) === 'tsproducts' ? (
        <>
          <Grid style={{ gridTemplateColumns: '1fr 2fr 1fr' }}>
            <ReusableSelect {...selectConfig.evp} />
            <ReusableSelect {...selectConfig.business} />
            <ReusableSelect {...selectConfig.region} />
          </Grid>
          <Grid columns={3}>
            <ReusableSelect {...selectConfig.country} />
            <ReusableSelect {...selectConfig.commodity} />
            <ReusableSelect {...selectConfig.carbonCredit} />
          </Grid>
        </>
      ) : normalizeEvpName(state.evp) === 'slmt' ? (
        <>
          <Grid columns={3}>
            <ReusableSelect {...selectConfig.evp} />
            <ReusableSelect {...selectConfig.business} />
            <ReusableSelect {...selectConfig.typeOfSupply} />
          </Grid>
          <Grid columns={2}>
            <ReusableSelect {...selectConfig.country} />
            <ReusableSelect {...selectConfig.commodity} />
          </Grid>
        </>
      ) : (
        <>
          <Grid columns={4}>
            <ReusableSelect {...selectConfig.evp} />
            <ReusableSelect {...selectConfig.business} />
            <ReusableSelect {...selectConfig.region} />
            <ReusableSelect {...selectConfig.country} />
          </Grid>
          <Grid columns={3}>
            <ReusableSelect {...selectConfig.commodity} />
            <ReusableSelect {...selectConfig.techSource} />
            <ReusableSelect {...selectConfig.gridLocation} />
          </Grid>
        </>
      )}
      {myState.showGasFields ? (
        <Grid columns={4}>
          <ReusableSelect {...selectConfig.assetType} />
          <ReusableSelect {...selectConfig.fuelType} />
          <ReusableSelect {...selectConfig.operatingModel} />
          <Flexbox columnGap='12px'>
            <FormField id='equityPercentage' label='Equity Share (%)' mandatory={true}>
              <TextInput
                aria-label='Equity Share'
                onChange={(e: TChangeEvent) => {
                  onEquityPercentageChange(e, dispatch, myDispatch);
                }}
                value={myState.equityShareVal}
                disabled={!myState.showEquityShare}
                type='number'
                onInput={() => maxLengthCheck}
                maxLength={2}
                invalid={!myState.isEquityPercentageValid}
              />
            </FormField>
            <FormField id='efficiencyPercentage' label='Efficiency (LHV%)' mandatory={true}>
              <TextInput
                aria-label='Efficiency'
                type='number'
                onChange={(e: TChangeEvent) => {
                  onEfficiencyPercentageChange(e, dispatch, myDispatch);
                }}
                value={myState.efficiencyVal}
                invalid={!myState.isEfficiencyValid}
              />
            </FormField>
          </Flexbox>
        </Grid>
      ) : null}
      <Grid columns={normalizeEvpName(state.evp) === 'slmt' ? 3 : 4}>
        <ReusableSelect {...selectConfig.unitOfMeasure} />
        {normalizeEvpName(state.evp) !== 'slmt' && (
          <FormField id='grossMargin' label={isTSProducts ? 'Gross Margin (Thousand USD)' : 'Gross Margin (Million USD)'} mandatory={true}>
            <TextInput
              aria-label='Gross Margin'
              type='text'
              onChange={(e: TChangeEvent) => {
                onGrossMarginChange(e, dispatch, myDispatch);
              }}
              value={myState.grossMarginVal}
              invalid={!myState.isGrossMarginValid}
            />
          </FormField>
        )}
        <FormField id='startDate' label='Start Date' mandatory={true}>
          <SingleDatePicker
            // format='MM/DD/YYYY'
            value={getDayjsFormat(myState.startDate)}
            aria-label='Pick date for the story StartDate'
            placeholder='DD MMM YYYY'
            disabledDate={(current) =>
              current != null &&
              (current.isBefore(dayjs().startOf('year')) || current.isAfter(dayjs('2050-12-31')))
            }
            onChange={(e) => {
              handleStartDateChange(e, dispatch, myDispatch);
            }}
            invalid={!myState.isStartDateValid}
            elevation={true}
          />
        </FormField>
        <FormField id='endDate' label='End Date' mandatory={true}>
          <SingleDatePicker
            // format='MM/DD/YYYY'
            value={getDayjsFormat(myState.endDate)}
            aria-label='Pick date for the story EndDate'
            placeholder='DD MMM YYYY'
            disabledDate={(current) =>
              current != null &&
              (current.isBefore(myState.startDate, 'date') || current.isAfter(dayjs('2050-12-31')))
            }
            onChange={(e) => {
              handleEndDateChange(e, dispatch, myDispatch);
            }}
            invalid={!myState.isEndDateValid}
            elevation={true}
          />
        </FormField>
      </Grid>
    </>
  );
};
